import * as React from 'react';
import BasicLayout from 'components/layout/BasicLayout';
import { PageWrapper } from 'components/common';
import { Card, CardBody } from 'components/common/Card';
import { useState } from 'react';
import { useEffect } from 'react';
import {
    CategoryAndGroupAggregations,
    GroupAggregation,
    GroupsByCategories,
    PageResponse
} from 'components/context/types';
import { Table, Icon } from 'antd';
import styled from 'styled-components';
import { formatPercentage } from 'utils/formatting';
import t from 'utils/translations';
import CreateGroupModal from 'components/pages/grouppage/CreateGroupModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CompanyToGroupModal from 'components/common/modal/CompanyToGroupModal';
import CategoryActionMenu, {
    CategoryActionMenuProps
} from 'components/pages/grouplistpage/actionmenu/CategoryActionMenu';
import GroupActionMenu, {
    GroupActionMenuProps
} from 'components/pages/grouplistpage/actionmenu/GroupActionMenu';
import { apiGetCategoryAndGroupAggregations } from 'api/categories';
import {
    customExpandIcon,
    NameInfo,
    RouterLinkTable
} from 'components/CommonGroupAndCategoryStuff/GroupsTableHelpers';
import BreadcrumbsTitle from 'components/CommonGroupAndCategoryStuff/BreadcrumbsTitle';
import { AddButton } from 'components/common/modal/ModalCommon';
import EmptyList from './EmptyList';

const HeaderWrapper = styled.div`
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-bottom: 12px;
`;

const StyledTable = styled(Table)`
    td:last-of-type {
        text-align: right;
    }
`;

export const NO_CATEGORY_ID = 'no-category';

const groupTableColumns = [
    {
        title: t('NAME'),
        dataIndex: 'name',
        key: 'name',
        render: ({ name, link }: NameInfo) => {
            return (
                <RouterLinkTable to={link ? link : '/'}>{name}</RouterLinkTable>
            );
        }
    },
    {
        title: t('AMOUNT_COMPANIES'),
        dataIndex: 'amount',
        key: 'amount'
    },
    {
        title: t('GROWTH_PERCENTAGE'),
        dataIndex: 'turnoverGrowthPercent',
        key: 'turnoverGrowthPercent',
        render: (turnoverGrowthPercent: number) => {
            return turnoverGrowthPercent
                ? formatPercentage(turnoverGrowthPercent, 2)
                : undefined;
        }
    },
    {
        title: t('PROFITABILITY'),
        dataIndex: 'returnOnInvestmentPercent',
        key: 'returnOnInvestmentPercent',
        render: (returnOnInvestmentPercent: number) => {
            return returnOnInvestmentPercent
                ? formatPercentage(returnOnInvestmentPercent, 2)
                : undefined;
        }
    },
    {
        title: t('NET_PROFIT_PER_TURNOVER'),
        dataIndex: 'netProfitPerTurnover',
        key: 'netProfitPerTurnover',
        render: (netProfitPerTurnover: number) => {
            return netProfitPerTurnover
                ? formatPercentage(netProfitPerTurnover, 2)
                : undefined;
        }
    },
    {
        dataIndex: 'action',
        key: 'action',
        render: (
            actionMenuProps: CategoryActionMenuProps | GroupActionMenuProps
        ) => {
            if ('numberOfGroupsInCategory' in actionMenuProps) {
                return <CategoryActionMenu {...actionMenuProps} />;
            } else {
                return <GroupActionMenu {...actionMenuProps} />;
            }
        }
    }
];

const buildRowData = (
    categoryAndGroupAggregations: CategoryAndGroupAggregations[],
    groupsByCategories: GroupsByCategories[],
    afterActionsFunction: () => void
): any[] => {
    const unCategorizedCategory = categoryAndGroupAggregations.find(
        category => category.categoryId === NO_CATEGORY_ID
    );

    const unCategorizedGroupAggs = unCategorizedCategory
        ? unCategorizedCategory.groupAggregations
        : [];

    const unCategorizedRows = buildGroupRows(
        unCategorizedGroupAggs,
        groupsByCategories,
        afterActionsFunction
    );

    const rowData = categoryAndGroupAggregations
        .filter(cat => cat.categoryId !== NO_CATEGORY_ID)
        .map(cat => {
            const categoryAggregation = cat.categoryAggregation;

            return {
                key: cat.categoryId,
                name: {
                    name: cat.categoryName,
                    link: `/kategoria/${cat.categoryId}`
                } as NameInfo,
                amount: cat.groupAggregations.reduce(
                    (sum, group) => sum + group.numberOfCompanies,
                    0
                ),
                turnoverGrowthPercent: categoryAggregation
                    ? categoryAggregation.turnoverGrowthPercent
                    : undefined,
                returnOnInvestmentPercent: categoryAggregation
                    ? categoryAggregation.returnOnInvestmentPercent
                    : undefined,
                netProfitPerTurnover: categoryAggregation
                    ? categoryAggregation.netProfitPerTurnover
                    : undefined,
                action: {
                    afterActionsFunction,
                    groupsByCategories,
                    id: cat.categoryId,
                    name: cat.categoryName,
                    numberOfGroupsInCategory: cat.numberOfGroups,
                    activatingElement: <Icon type="setting" theme="filled" />
                } as CategoryActionMenuProps,
                children: buildGroupRows(
                    cat.groupAggregations,
                    groupsByCategories,
                    afterActionsFunction,
                    cat.categoryId
                )
            };
        });

    rowData.unshift(...unCategorizedRows);

    return rowData;
};

const buildGroupRows = (
    groupAggregations: GroupAggregation[],
    groupsByCategories: GroupsByCategories[],
    afterActionsFunction: () => void,
    categoryId?: string
): any[] => {
    return groupAggregations.map((agg: GroupAggregation) => {
        const groupAggregation = agg.groupAggregation;

        return {
            key: agg.groupId,
            name: {
                name: agg.groupName,
                link: categoryId
                    ? `/kategoria/${categoryId}/${agg.groupId}`
                    : `/ryhma/${agg.groupId}`
            } as NameInfo,
            amount: agg.numberOfCompanies,
            turnoverGrowthPercent: groupAggregation
                ? groupAggregation.turnoverGrowthPercent
                : undefined,
            returnOnInvestmentPercent: groupAggregation
                ? groupAggregation.returnOnInvestmentPercent
                : undefined,
            netProfitPerTurnover: groupAggregation
                ? groupAggregation.netProfitPerTurnover
                : undefined,
            action: {
                afterActionsFunction,
                groupsByCategories,
                id: agg.groupId,
                name: agg.groupName,
                numberOfCompaniesInGroup: agg.numberOfCompanies,
                activatingElement: <Icon type="setting" theme="filled" />
            } as GroupActionMenuProps
        };
    });
};

function GroupListPage() {
    const [isLoading, setIsLoading] = useState(true);

    const [
        categoryAndGroupAggregations,
        setCategoryAndGroupAggregations
    ] = useState<CategoryAndGroupAggregations[]>([]);

    const [addCompanyModalVisible, setAddCompanyModalVisible] = useState<
        boolean
    >(false);

    const [isCreateGroupModalVisible, setIsCreateGroupModalVisible] = useState(
        false
    );

    useEffect(() => {
        fetchCategoryAndGroupAggregations();
    }, []);

    function fetchCategoryAndGroupAggregations() {
        setIsLoading(true);
        apiGetCategoryAndGroupAggregations()
            .then(res => res.json())
            .then((data: PageResponse<CategoryAndGroupAggregations>) => {
                setCategoryAndGroupAggregations(data.content);
            })
            .finally(() => setIsLoading(false));
    }

    const groupsByCategories = categoryAndGroupAggregations.map(catAgg => ({
        categoryName: catAgg.categoryName,
        categoryId: catAgg.categoryId,
        groups: catAgg.groupAggregations.map(groupAgg => ({
            groupId: groupAgg.groupId,
            groupName: groupAgg.groupName,
            categoryName: catAgg.categoryName
        }))
    }));

    return (
        <BasicLayout>
            <CreateGroupModal
                afterSuccess={() => fetchCategoryAndGroupAggregations()}
                categories={categoryAndGroupAggregations.map(agg => ({
                    categoryName: agg.categoryName,
                    categoryId: agg.categoryId
                }))}
                closeModal={() => setIsCreateGroupModalVisible(false)}
                visible={isCreateGroupModalVisible}
            />
            <PageWrapper>
                <HeaderWrapper>
                    <BreadcrumbsTitle
                        breadcrumbItems={[{ text: t('GROUPS_TITLE') }]}
                    />
                    <div>
                        <AddButton
                            type="primary"
                            onClick={() => setAddCompanyModalVisible(true)}
                        >
                            <FontAwesomeIcon icon={['fas', 'plus']} />
                            Lisää yrityksiä ryhmään
                        </AddButton>
                        <AddButton
                            onClick={() => setIsCreateGroupModalVisible(true)}
                            type="primary"
                        >
                            <FontAwesomeIcon icon={['fas', 'plus']} />
                            {t('CREATE_NEW_GROUP')}
                        </AddButton>
                    </div>
                </HeaderWrapper>
                <CompanyToGroupModal
                    afterSuccess={() => fetchCategoryAndGroupAggregations()}
                    visible={addCompanyModalVisible}
                    groupsByCategories={groupsByCategories}
                    closeModal={() => setAddCompanyModalVisible(false)}
                />
                <Card>
                    <CardBody>
                        <section>
                            <StyledTable
                                tableLayout={'auto'}
                                locale={{
                                    emptyText: <EmptyList />
                                }}
                                columns={groupTableColumns}
                                expandIcon={props => customExpandIcon(props)}
                                dataSource={buildRowData(
                                    categoryAndGroupAggregations,
                                    groupsByCategories,
                                    () => fetchCategoryAndGroupAggregations()
                                )}
                                indentSize={50}
                                loading={isLoading}
                                pagination={false} // NOTE: there is currently no pagination on this page
                            />
                        </section>
                    </CardBody>
                </Card>
            </PageWrapper>
        </BasicLayout>
    );
}

export default GroupListPage;
